import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Mail, Phone, CreditCard, Calendar } from 'lucide-react';
import BarcodeModal from '../components/ui/BarcodeModal';
import EditMemberModal from '../components/members/EditMemberModal';
import PrintMemberCard from '../components/ui/PrintMemberCard';
import { getMember, getMemberFines } from '../api/members';

const AVBG = ['var(--accent-bg)','var(--success-bg)','var(--warning-bg)','#1a0d2e','#0a1f2e'];
const AVCO = ['var(--accent)','var(--success)','var(--warning)','#a78bfa','#38bdf8'];

export default function MemberDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('info');
  const [showBarcode, setShowBarcode] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['member', id],
    queryFn: () => getMember(Number(id)),
  } as any);

  const { data: finesData } = useQuery({
    queryKey: ['member-fines', id],
    queryFn: () => getMemberFines(Number(id)),
    enabled: activeTab === 'fines',
  } as any);

  if (isLoading) return <div style={{padding:24,color:'var(--text-faint)',fontFamily:'system-ui,sans-serif'}}>იტვირთება...</div>;

  const member = (data as any)?.data?.member || (data as any)?.data;
  if (!member) return null;

  const ci = (Number(id)||0) % 5;
  const checkouts = (data as any)?.data?.checkouts || member.checkouts || [];
  const fines = (finesData as any)?.data?.fines || [];
  const totalFines = fines.reduce((s: number, f: any) => s + (f.amount - f.paid_amount), 0);

  const tabs = [
    { key: 'info', label: 'ინფო', icon: 'ti-user' },
    { key: 'checkouts', label: `გაცემები (${checkouts.length})`, icon: 'ti-arrows-exchange' },
    { key: 'fines', label: 'ჯარიმები', icon: 'ti-coin' },
  ];

  return (
    <div style={{padding:24,fontFamily:'system-ui,sans-serif',maxWidth:960,margin:'0 auto'}}>
      <BarcodeModal type="member" id={Number(id)} open={showBarcode} onClose={() => setShowBarcode(false)} />
      <EditMemberModal open={showEdit} onClose={() => setShowEdit(false)} member={member} />
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:24}}>
        <button onClick={() => navigate('/members')}
          style={{display:'flex',alignItems:'center',gap:6,background:'var(--bg-dark2)',border:'1px solid #1e2d45',borderRadius:8,padding:'7px 12px',fontSize:12,color:'var(--text-muted)',cursor:'pointer'}}>
          <ArrowLeft size={14} /> წევრები
        </button>
        <div style={{marginLeft:'auto',display:'flex',gap:8}}>
          <PrintMemberCard member={member} />
          <button onClick={() => setShowBarcode(true)} style={{display:'flex',alignItems:'center',gap:6,background:'var(--bg-dark2)',border:'1px solid #1e2d45',borderRadius:8,padding:'7px 12px',fontSize:12,color:'#a78bfa',cursor:'pointer'}}>
            <i className="ti ti-barcode" style={{fontSize:14}} /> ბარკოდი
          </button>
          <button onClick={() => setShowEdit(true)} style={{display:'flex',alignItems:'center',gap:6,background:'var(--bg-dark2)',border:'1px solid #1e2d45',borderRadius:8,padding:'7px 12px',fontSize:12,color:'var(--accent)',cursor:'pointer'}}>
            <i className="ti ti-edit" style={{fontSize:14}} /> რედაქტირება
          </button>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:24,marginBottom:24}}>
        <div style={{width:100,height:100,borderRadius:20,background:AVBG[ci],border:`2px solid ${AVCO[ci]}44`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:40,fontWeight:700,color:AVCO[ci],flexShrink:0}}>
          {(member.first_name?.[0]||'?').toUpperCase()}
        </div>
        <div>
          <h1 style={{fontSize:22,fontWeight:600,color:'var(--text-primary)',margin:'0 0 4px'}}>
            {member.last_name} {member.first_name}
          </h1>
          <p style={{fontSize:13,color:'var(--text-muted)',margin:'0 0 16px'}}>{member.member_type||'წევრი'}</p>

          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:16}}>
            <span style={{padding:'4px 10px',borderRadius:6,fontSize:12,
              background:member.is_active?'var(--success-bg)':'var(--danger-bg)',
              color:member.is_active?'var(--success)':'var(--danger)',
              border:`1px solid ${member.is_active?'#0a4a2a':'#5a1a1a'}`}}>
              {member.is_active ? 'აქტიური' : 'გათიშული'}
            </span>
            {member.active_checkouts > 0 && (
              <span style={{padding:'4px 10px',borderRadius:6,fontSize:12,background:'var(--warning-bg)',color:'var(--warning)',border:'1px solid #4a3010'}}>
                {member.active_checkouts} გაცემული
              </span>
            )}
          </div>

          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
            {[
              {icon:<Mail size={13}/>, label:'ელ-ფოსტა', val:member.email||'—'},
              {icon:<Phone size={13}/>, label:'ტელეფონი', val:member.phone||'—'},
              {icon:<CreditCard size={13}/>, label:'ბარკოდი', val:member.barcode||'—'},
              {icon:<Calendar size={13}/>, label:'რეგისტრაცია', val:member.created_at?new Date(member.created_at).toLocaleDateString():'—'},
            ].map(f => (
              <div key={f.label} style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:8,padding:'10px 12px'}}>
                <div style={{display:'flex',alignItems:'center',gap:5,fontSize:10,color:'var(--text-faint)',marginBottom:4}}>
                  {f.icon} {f.label}
                </div>
                <div style={{fontSize:12,color:'var(--text-secondary)',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.val}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
        <div style={{display:'flex',borderBottom:'1px solid #1e2d45'}}>
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{padding:'12px 20px',background:'none',border:'none',borderBottom:activeTab===tab.key?'2px solid #60a5fa':'2px solid transparent',marginBottom:-1,fontSize:13,color:activeTab===tab.key?'var(--accent)':'var(--text-faint)',cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontWeight:activeTab===tab.key?500:400}}>
              <i className={`ti ${tab.icon}`} style={{fontSize:15}} />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'info' && (
          <div style={{padding:20}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              {[
                {label:'სახელი', val:member.first_name||'—'},
                {label:'გვარი', val:member.last_name||'—'},
                {label:'ელ-ფოსტა', val:member.email||'—'},
                {label:'ტელეფონი', val:member.phone||'—'},
                {label:'მისამართი', val:member.address||'—'},
                {label:'პირადი №', val:member.id_number||'—'},
              ].map(f => (
                <div key={f.label} style={{background:'var(--bg-input)',border:'1px solid #1e2d45',borderRadius:8,padding:'12px 14px'}}>
                  <div style={{fontSize:11,color:'var(--text-faint)',marginBottom:4}}>{f.label}</div>
                  <div style={{fontSize:13,color:'var(--text-secondary)'}}>{f.val}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'checkouts' && (
          <div>
            {checkouts.length === 0
              ? <p style={{padding:24,textAlign:'center',color:'var(--text-dimmed)',fontSize:13}}>გაცემა არ არის</p>
              : checkouts.map((c: any) => {
                  const isOverdue = !c.returned_at && new Date(c.due_date) < new Date();
                  return (
                    <div key={c.id} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 20px',borderBottom:'1px solid #0f1826'}}>
                      <div style={{flex:1}}>
                        <div style={{fontSize:13,color:'var(--text-secondary)',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:400}}>{c.title}</div>
                        <div style={{fontSize:11,color:'var(--text-faint)',marginTop:2}}>{c.author}</div>
                      </div>
                      <div style={{fontSize:11,color:'var(--text-dimmed)',textAlign:'right'}}>
                        <div>{new Date(c.checked_out_at).toLocaleDateString()}</div>
                        <div style={{color:isOverdue?'var(--danger)':'var(--text-faint)'}}>→ {new Date(c.due_date).toLocaleDateString()}</div>
                      </div>
                      <span style={{padding:'3px 10px',borderRadius:6,fontSize:11,fontWeight:500,flexShrink:0,
                        background:c.returned_at?'var(--success-bg)':isOverdue?'var(--danger-bg)':'var(--warning-bg)',
                        color:c.returned_at?'var(--success)':isOverdue?'var(--danger)':'var(--warning)'}}>
                        {c.returned_at?'დაბრუნდა':isOverdue?'ვადაგადაცილებული':'აქტიური'}
                      </span>
                    </div>
                  );
                })
            }
          </div>
        )}

        {activeTab === 'fines' && (
          <div>
            {totalFines > 0 && (
              <div style={{margin:16,padding:14,background:'var(--danger-bg)',border:'1px solid #5a1a1a',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <span style={{fontSize:13,color:'var(--danger)'}}>სულ გადასახდელი ჯარიმა</span>
                <span style={{fontSize:18,fontWeight:700,color:'var(--danger)'}}>₾{totalFines.toFixed(2)}</span>
              </div>
            )}
            {fines.length === 0
              ? <p style={{padding:24,textAlign:'center',color:'var(--text-dimmed)',fontSize:13}}>ჯარიმა არ არის</p>
              : fines.map((f: any) => (
                <div key={f.id} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 20px',borderBottom:'1px solid #0f1826'}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,color:'var(--text-secondary)'}}>{f.reason||'ჯარიმა'}</div>
                    <div style={{fontSize:11,color:'var(--text-faint)',marginTop:2}}>{new Date(f.created_at).toLocaleDateString()}</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:14,fontWeight:600,color:'var(--danger)'}}>₾{f.amount}</div>
                    {f.paid_amount > 0 && <div style={{fontSize:11,color:'var(--success)'}}>გადახდილი: ₾{f.paid_amount}</div>}
                  </div>
                  <span style={{padding:'3px 10px',borderRadius:6,fontSize:11,fontWeight:500,
                    background:f.paid_amount>=f.amount?'var(--success-bg)':'var(--danger-bg)',
                    color:f.paid_amount>=f.amount?'var(--success)':'var(--danger)'}}>
                    {f.paid_amount>=f.amount?'გადახდილი':'გადაუხდელი'}
                  </span>
                </div>
              ))
            }
          </div>
        )}
      </div>
    </div>
  );
}
